// Validator types
interface ValidationResult {
  valid: boolean;
  errors?: string[];
}

interface NodeValidator {
  validate(parameters: any): ValidationResult;
}

interface ScheduleItem {
  mode: 'everyXMinutes' | 'everyHour' | 'everyXHours' | 'everyDay' | 'everyWeek' | 'everyMonth' | 'custom';
  minuteInterval?: number;
  hourInterval?: number;
  startHour?: number;
  minute?: number;
  hour?: number;
  dayOfWeek?: number;
  dayOfMonth?: number;
  cronExpression?: string;
  restrictToTimeRange?: boolean;
  timeRangeStart?: number;
  timeRangeEnd?: number;
  restrictToDays?: boolean;
  daysOfWeek?: number[];
}

interface ScheduleTriggerParameters {
  triggerTimes?: {
    item?: ScheduleItem[];
  };
  timezone?: string;
}

export const scheduleTriggerValidator: NodeValidator = {
  validate: (parameters: unknown): ValidationResult => {
    const params = parameters as ScheduleTriggerParameters;
    const errors: string[] = [];

    // Check if schedules exist
    if (!params.triggerTimes?.item || params.triggerTimes.item.length === 0) {
      errors.push('At least one schedule must be configured');
      return { valid: false, errors };
    }

    // Validate each schedule
    params.triggerTimes.item.forEach((schedule, index) => {
      const prefix = `Schedule ${index + 1}:`;

      switch (schedule.mode) {
        case 'everyXMinutes':
          if (!schedule.minuteInterval || ![5, 10, 15, 20, 30].includes(schedule.minuteInterval)) {
            errors.push(`${prefix} Invalid minute interval`);
          }
          break;

        case 'everyHour':
          if (schedule.minute === undefined || schedule.minute < 0 || schedule.minute > 59) {
            errors.push(`${prefix} Minute must be between 0 and 59`);
          }
          break;

        case 'everyXHours':
          if (!schedule.hourInterval || ![2, 3, 4, 6, 12].includes(schedule.hourInterval)) {
            errors.push(`${prefix} Invalid hour interval`);
          }
          if (schedule.startHour !== undefined && (schedule.startHour < 0 || schedule.startHour > 23)) {
            errors.push(`${prefix} Start hour must be between 0 and 23`);
          }
          break;

        case 'everyDay':
          if (schedule.hour === undefined || schedule.hour < 0 || schedule.hour > 23) {
            errors.push(`${prefix} Hour must be between 0 and 23`);
          }
          if (schedule.minute === undefined || schedule.minute < 0 || schedule.minute > 59) {
            errors.push(`${prefix} Minute must be between 0 and 59`);
          }
          break;

        case 'everyWeek':
          if (schedule.hour === undefined || schedule.hour < 0 || schedule.hour > 23) {
            errors.push(`${prefix} Hour must be between 0 and 23`);
          }
          if (schedule.minute === undefined || schedule.minute < 0 || schedule.minute > 59) {
            errors.push(`${prefix} Minute must be between 0 and 59`);
          }
          if (schedule.dayOfWeek === undefined || schedule.dayOfWeek < 0 || schedule.dayOfWeek > 6) {
            errors.push(`${prefix} Day of week must be between 0 and 6`);
          }
          break;

        case 'everyMonth':
          if (schedule.hour === undefined || schedule.hour < 0 || schedule.hour > 23) {
            errors.push(`${prefix} Hour must be between 0 and 23`);
          }
          if (schedule.minute === undefined || schedule.minute < 0 || schedule.minute > 59) {
            errors.push(`${prefix} Minute must be between 0 and 59`);
          }
          if (schedule.dayOfMonth === undefined || schedule.dayOfMonth < 1 || schedule.dayOfMonth > 31) {
            errors.push(`${prefix} Day of month must be between 1 and 31`);
          }
          break;

        case 'custom':
          if (!schedule.cronExpression) {
            errors.push(`${prefix} Cron expression is required`);
          } else {
            // Basic cron validation (5 parts)
            const parts = schedule.cronExpression.trim().split(/\s+/);
            if (parts.length !== 5) {
              errors.push(`${prefix} Cron expression must have 5 parts (minute hour day month dayOfWeek)`);
            }
          }
          break;

        default:
          errors.push(`${prefix} Unknown mode: ${schedule.mode}`);
      }

      // Validate time range restrictions
      if (schedule.restrictToTimeRange) {
        if (schedule.timeRangeStart === undefined || schedule.timeRangeStart < 0 || schedule.timeRangeStart > 23) {
          errors.push(`${prefix} Time range start must be between 0 and 23`);
        }
        if (schedule.timeRangeEnd === undefined || schedule.timeRangeEnd < 0 || schedule.timeRangeEnd > 23) {
          errors.push(`${prefix} Time range end must be between 0 and 23`);
        }
        if (schedule.timeRangeStart !== undefined && schedule.timeRangeEnd !== undefined) {
          if (schedule.timeRangeStart >= schedule.timeRangeEnd) {
            errors.push(`${prefix} Time range start must be before end`);
          }
        }
      }

      // Validate day restrictions
      if (schedule.restrictToDays) {
        if (!schedule.daysOfWeek || schedule.daysOfWeek.length === 0) {
          errors.push(`${prefix} At least one day must be selected`);
        } else {
          const invalidDays = schedule.daysOfWeek.filter(d => d < 0 || d > 6);
          if (invalidDays.length > 0) {
            errors.push(`${prefix} Invalid days of week: ${invalidDays.join(', ')}`);
          }
        }
      }
    });

    return {
      valid: errors.length === 0,
      errors: errors.length > 0 ? errors : undefined
    };
  }
};

export default scheduleTriggerValidator;
